import React, { useEffect, useState } from "react";
import {
  Stack,
  InputLabel,
  TextField,
  Button,
} from "@mui/material";

import { ValidationError } from "../app/errors";

const OTP = (props) => {
  const [hasError, setHasError] = useState(false);

  const inputRefs = React.useRef([]);

  useEffect(() => {
    for (let i = 0; i < props.length; i++) {
      inputRefs.current[i].value = "";
    }
  }, [props.length, props.reset]);

  useEffect(() => {
    setHasError(!!props.error);
  }, [props.error]);

  const updateCode = (index, e) => {
    e.preventDefault();

    if (/([0-9])/.test(e.key)) {
      inputRefs.current[index].value = e.key;

      if (index < props.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    }

    const code = inputRefs.current.map(v => v.value);

    if (code.every(v => v !== "")) {
      props.onValidate(code.join(""));
    }
  }

  const style = {
    textAlign: "center",
    fontSize: "1em",
    color: hasError ? "red" : "initial",
    WebkitTextFillColor: hasError ? "red" : "initial",
  };

  return (
    <Stack direction="row" justifyContent="space-evenly" spacing={1} style={{ width: "50%" }}>
      {new Array(props.length).fill(0).map((item, index) =>
        <TextField
          key={index}
          variant="outlined"
          size="small"
          inputProps={{ maxLength: 1, style }}
          inputRef={(element) => { inputRefs.current[index] = element }}
          onKeyDown={e => updateCode(index, e)}
          error={hasError}
          disabled={props.disabled}
          autoComplete="off"
        />
      )}
    </Stack>
  );
}

const styles = {
  email: {
    minWidth: "350px"
  }
}

export const Email = ({
  value,
  disabled,
  onValidateEmail,
  onValidateCode,
  onChange
}) => {
  const [email, setEmail] = useState(value);
  const [isValidatingEmail, setIsValidatingEmail] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isCodeValid, setIsCodeValid] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [resetOTP, performOTPReset] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
  }, [email]);

  useEffect(() => {
    if (value) {
      setIsEmailValid(true);
      setIsCodeValid(true);
    }
  }, [value]);

  const reset = () => {
    setIsEmailValid(false);
    setIsCodeValid(false);
    setEmail("");
    setError("");
    onChange("");
  }

  const validateEmail = async () => {
    setIsValidatingEmail(true);

    try {
      await onValidateEmail(email);
      setIsEmailValid(true);
    } catch (e) {
      if (e instanceof ValidationError) {
        setError(e.message);
      } else {
        console.error(e);
        setError(`An unknown error occured. Please try again.`);
      }
    }

    setIsValidatingEmail(false);
  }

  const validateOTP = async (code) => {
    try {
      setIsValidatingCode(true);
      await onValidateCode(code);
      setIsCodeValid(true);
      onChange(email);
    } catch (e) {
      setError(e.message);
      setIsCodeValid(false);

      if (e.error === "missing_arguments") {
        reset();
      } else if (e.error === "otp_expired") {
        resendOTP();
      } else {
        setTimeout(() => {
          setIsCodeValid(false);
          setError("");
          performOTPReset(prev => prev + 1);
        }, 1000);
      }
    } finally {
      setIsValidatingCode(false);
    }
  }

  const resendOTP = async () => {
    setError("");
    await onValidateEmail(email);
    performOTPReset(prev => prev + 1);
  }

  return (
    <Stack direction="column" spacing={2}>
      <InputLabel sx={{fontWeight: 'bold', color: '#000'}}>Email *</InputLabel>

      <Stack direction="column" spacing={2}>
        {(!isEmailValid && !isCodeValid) && (
          <Stack direction="column" spacing={2}>
            <TextField
              style={styles.email}
              variant="outlined"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={disabled || isValidatingEmail}
              error={!!error}
              helperText={error}
              autoComplete="email"
            />
            <Button variant="outlined" onClick={validateEmail} disabled={disabled} sx={{whiteSpace: 'nowrap'}}>Send email validation</Button>
          </Stack>
        )}

        {(isEmailValid && !isCodeValid) && (
          <Stack direction="row" spacing={2}>
            <OTP
              length={4}
              disabled={isValidatingCode || !!error}
              reset={resetOTP}
              error={error}
              onValidate={validateOTP} />
            <Button onClick={resendOTP}>Resend</Button>
          </Stack>
        )}

        {(isEmailValid && isCodeValid) && (
          <Stack direction="row" spacing={2}>
            <TextField
              variant="outlined"
              value={email}
              style={styles.email}
              disabled />
            <Button onClick={reset}>Reset</Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}